import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Container, Divider, Typography } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import usePageTitle from '../../hooks/usePageTitle';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(12),
    background: theme.palette.background.default
  },
  heroSection: {
    textAlign: 'center',
    marginBottom: theme.spacing(8)
  },
  heroTitle: {
    fontWeight: 700,
    fontSize: '3rem',
    lineHeight: 1.2,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem'
    }
  },
  heroSubtitle: {
    fontSize: '1.25rem',
    color: theme.palette.text.secondary,
    maxWidth: '700px',
    margin: '0 auto'
  },
  featureSection: {
    marginBottom: theme.spacing(10),
    scrollMarginTop: '100px'
  },
  featureTitle: {
    fontWeight: 600,
    fontSize: '2rem',
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem'
    }
  },
  featureDescription: {
    fontSize: '1.125rem',
    lineHeight: 1.8,
    color: theme.palette.text.secondary,
    maxWidth: '900px'
  },
  divider: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(8)
  }
}));

const Products = () => {
  const classes = useStyles();

  usePageTitle('Platform Features - Remote Job');

  useEffect(() => {
    // Scroll to anchor if present in URL
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Remote Hiring Platform Features | Remote Job</title>
        <meta
          name="description"
          content="Streamline remote hiring with candidate management, application tracking, built-in messaging, interview scheduling, and geo-location filtering. All-in-one platform for distributed teams."
        />
        <meta
          name="keywords"
          content="remote hiring, applicant tracking system, candidate management, remote recruitment, hiring software, ATS, remote jobs platform, interview scheduling, remote work hiring"
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Remote Hiring Platform Features | Remote Job" />
        <meta
          property="og:description"
          content="Streamline remote hiring with candidate management, application tracking, built-in messaging, interview scheduling, and geo-location filtering."
        />
        <meta property="og:url" content="https://remotework.com/products" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Remote Hiring Platform Features | Remote Job" />
        <meta
          name="twitter:description"
          content="Streamline remote hiring with candidate management, application tracking, built-in messaging, interview scheduling, and geo-location filtering."
        />

        {/* Structured Data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Remote Job",
            "applicationCategory": "BusinessApplication",
            "description": "Remote hiring platform with candidate management, application tracking, messaging, interview scheduling, and geo-location filtering for distributed teams.",
            "offers": {
              "@type": "Offer",
              "category": "Subscription"
            },
            "featureList": [
              "Candidate Management",
              "Application Tracking",
              "Built-In Messaging",
              "Interview Scheduling",
              "Candidate Interview Scoring",
              "Geo-Location Filtering"
            ]
          })}
        </script>
      </Helmet>

      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box className={classes.heroSection}>
          <Typography className={classes.heroTitle} variant="h1">
            Platform Features
          </Typography>
          <Typography className={classes.heroSubtitle} variant="h5">
            Tools to manage your hiring pipeline and connect with candidates
          </Typography>
        </Box>

        {/* Feature 1: Candidate Management */}
        <Box id="candidate-management" className={classes.featureSection}>
          <Typography className={classes.featureTitle} variant="h2">
            Candidate Management
          </Typography>
          <Typography className={classes.featureDescription}>
            Manage your hiring pipeline with a visual board that organizes
            candidates by stage. Move applicants from review to interview to
            offer, and access all candidate information, activity, and notes in
            one place. Your entire hiring team can see where each candidate
            stands and coordinate next steps.
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        {/* Feature 2: Application Tracking */}
        <Box id="application-tracking" className={classes.featureSection}>
          <Typography className={classes.featureTitle} variant="h2">
            Application Tracking
          </Typography>
          <Typography className={classes.featureDescription}>
            View all applications across your open positions in a single
            dashboard. See application status, review timelines, and track
            candidate progress from submission to hire. Filter by job or status
            to quickly find what you need and keep your hiring pipeline moving.
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        {/* Feature 3: Built-In Messaging */}
        <Box id="built-in-messaging" className={classes.featureSection}>
          <Typography className={classes.featureTitle} variant="h2">
            Built-In Messaging
          </Typography>
          <Typography className={classes.featureDescription}>
            Communicate with candidates directly in the platform. Send messages,
            share updates, and answer questions without switching tools. All
            conversations stay organized by candidate and position, accessible
            to your hiring team.
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        {/* Feature 4: Interview Scheduling */}
        <Box id="interview-scheduling" className={classes.featureSection}>
          <Typography className={classes.featureTitle} variant="h2">
            Interview Scheduling
          </Typography>
          <Typography className={classes.featureDescription}>
            Schedule interviews and send automatic email confirmations to
            candidates and interviewers. Set dates, times, and details in one
            place, and track all interviews alongside candidate records for a
            complete view of your hiring timeline.
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        {/* Feature 5: Candidate Interview Scoring */}
        <Box
          id="candidate-interview-scoring"
          className={classes.featureSection}>
          <Typography className={classes.featureTitle} variant="h2">
            Candidate Interview Scoring
          </Typography>
          <Typography className={classes.featureDescription}>
            After each interview, interviewers can quickly assign a rating—Very
            Poor, Poor, Good, or Very Good. This score is tied to the
            candidate’s application, and each card maintains a history of all
            interview feedback. When revisiting a candidate, the team sees past
            scores at a glance, ensuring consistent and easy comparisons. It's a
            simple way to track impressions and keep your hiring process sharp.
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        {/* Feature 6: Geo-Location Filtering */}
        <Box id="geo-location-filtering" className={classes.featureSection}>
          <Typography className={classes.featureTitle} variant="h2">
            Geo-Location Filtering
          </Typography>
          <Typography className={classes.featureDescription}>
            Restrict job visibility by geographic region to target specific
            candidate markets. Control who can view and apply to your postings
            based on location, helping you focus on candidates in your preferred
            hiring regions and reduce time reviewing applications outside your
            target areas.
          </Typography>
        </Box>
      </Container>
    </div>
  );
};

export default Products;
